import { useCorporate } from "@/context/CorporateGroupContext";
import { Company } from "@/interfaces/Company";
import { ConfigComun } from "@/interfaces/Comun";
import { setDriverData } from "@/stores/features/driverQuestionSlice";
import {
  setDiagnosisCurrent,
  setNextDiagnosisCurrent,
  setNextExternalCurrent,
  setPrevDiagnosisCurrent,
} from "@/stores/features/utilsSlice";
import {
  syncChecklistIds,
  setCheckDiagnosisDataForChanges,
  fullResetDiagnosis,
} from "@/stores/features/diagnosisSlice";
import { setFleetData } from "@/stores/features/vehicleQuestionsSlice";
import { useAppDispatch, useAppSelector } from "@/stores/hooks";
import { companyService } from "@/stores/services/companyService";
import { corporateGroupService } from "@/stores/services/corporateGroupService";
// import { diagnosisService } from "@/stores/services/diagnosisServices";
import { diagnosisService } from "@/stores/services/diagnosisServices";
import { TOAST_TYPE, toastHandler } from "@/utils/useToast";
import { decryptId, encryptId } from "@/utils/utilsMethods";
import { skipToken } from "@reduxjs/toolkit/query";
import { Button, message, Segmented, Skeleton } from "antd";
import clsx from "clsx";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  transformDriverData,
  transformFleetData,
} from "../data/data-transform";
import { useDriverData } from "../hooks/useDriverData";
import { useFleetData } from "../hooks/useFleetData";
import CompanyHeaderInfo from "./CompanyHeaderInfo";
import ConsultandSelect from "./ConsultandSelect";
import ContinueOrSaveButton from "./ContinueOrSaveButton";
import MissionSelect from "./MissionSelect";

const PesvNoteAndSegmented = lazy(() => import("./PesvNoteAndSegmented"));

interface Props extends Partial<ConfigComun> {
  companyId: number;

  onlyInfo?: boolean;
  isOutOfContext?: boolean;
}
export default function CompanyInfo({
  companyId,
  onlyInfo,
  isOutOfContext = false,
  isExternal,
}: Props) {
  const { corporateId } = useCorporate();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const current = useAppSelector((state) => state.util.diagnosisCurrent);
  const stepsLenght = useAppSelector((state) => state.util.stepLenght);
  const isCompanyInfoCollapse = useAppSelector(
    (state) => state.util.companyInfoCollapse,
  );
  const [searchParams] = useSearchParams();
  const diagnosisParam = searchParams.get("diagnosis");
  const diagnosisId = diagnosisParam
    ? parseInt(decryptId(diagnosisParam))
    : undefined;

  useEffect(() => {
    if (diagnosisId === undefined) {
      dispatch(setDiagnosisCurrent(0));
    }
  }, [diagnosisId, dispatch]);

  const { fleetByCompany, isLoadingFleetByCompany } = useFleetData(
    companyId,
    diagnosisId,
  );

  const { driverByCompanyid, isLoadingDriverByCompany } = useDriverData(
    companyId,
    diagnosisId,
  );

  const fleetData = useMemo(() => {
    return fleetByCompany ? transformFleetData(fleetByCompany) : [];
  }, [fleetByCompany]);

  const driverCountData = useMemo(() => {
    return driverByCompanyid ? transformDriverData(driverByCompanyid) : [];
  }, [driverByCompanyid]);

  // 🔥 Actualizamos Redux SOLO cuando los datos realmente están listos
  useEffect(() => {
    if (!isLoadingFleetByCompany && fleetData.length > 0) {
      dispatch(setFleetData(fleetData));
    }

    if (!isLoadingDriverByCompany && driverCountData.length > 0) {
      dispatch(setDriverData(driverCountData));
    }
  }, [
    fleetData,
    driverCountData,
    isLoadingFleetByCompany,
    isLoadingDriverByCompany,
  ]);

  //#region states
  const [company, setCompany] = useState<Company | null>(null);
  const [consultorSelect, setConsultorSelect] = useState<number | null>(null);
  const [missionSelect, setMissionSelect] = useState<number | null>(null);
  const [size, setSize] = useState<number>(0);
  const [observationChanged, setObservationChanged] = useState<string | null>(
    null,
  );
  const [ejecution, setEjecution] = useState<string>("presencial");
  const [userChanged, setUserChanged] = useState<boolean>(false);
  //#endregion

  //#region querys
  const { data: corporateData } = corporateGroupService.useFindByIdQuery(
    corporateId ? { id: corporateId } : skipToken,
  );

  const { data, refetch, isUninitialized } = companyService.useFindByIdQuery(
    companyId ? { id: companyId } : skipToken,
  );
  const {
    data: diagnosisDataById,
    refetch: refetchDiagnosis,
    isUninitialized: unitializedDiagnosis,
  } = diagnosisService.useFindByIdQuery(
    diagnosisId
      ? { diagnosisId }
      : corporateId
        ? { diagnosisId: 0, corporate_group: corporateId }
        : skipToken,
  );
  //#endregion

  //#region mutations
  const [saveAnswerCuestions, { isLoading }] =
    diagnosisService.useSaveAnswerCuestionsMutation();
  // TODO: PENDIENTE ACTUALIZAR
  const [saveCountForCompanyInCorporate, { isLoading: corporateLoading }] =
    diagnosisService.useSaveCountForCompanyInCorporateMutation();
  // TODO: PENDIENTE ACTUALIZAR
  const [saveDiagnosis, { isLoading: saveProgressLoad }] =
    diagnosisService.useSaveDiagnosisMutation();
  // TODO: PENDIENTE ACTUALIZAR
  const [updateDiagnosis, { isLoading: updateDiagnosisLoading }] =
    diagnosisService.useUpdateDiagnosisMutation();

  const [updateCompany] = companyService.useUpdateCompanyMutation();
  //#endregion

  //Storage
  const totalVehicles = useAppSelector(
    (state) => state.vehicleQuestion.totalQuantity,
  );
  const totalDrivers = useAppSelector(
    (state) => state.driverQuestion.totalQuantity,
  );

  useEffect(() => {
    if (data) setCompany(data);
  }, [data]);

  useEffect(() => {
    if (diagnosisDataById) {
      if (!isOutOfContext) {
        setSize(diagnosisDataById.diagnosis.type?.id ?? 0);
      }
      setObservationChanged(diagnosisDataById.diagnosis.observation);
      setConsultorSelect(diagnosisDataById.diagnosis.consultant?.id ?? null);
      setEjecution(diagnosisDataById.diagnosis.mode_ejecution ?? "presencial");
    }
  }, [diagnosisDataById]);

  useEffect(() => {
    if (!isUninitialized) refetch();
  }, [isUninitialized, refetch]);

  useEffect(() => {
    if (!unitializedDiagnosis) refetchDiagnosis();
  }, [unitializedDiagnosis, refetchDiagnosis]);

  useEffect(() => {
    if (company && company.mission) setMissionSelect(company.mission.id);
  }, [company]);

  const onMissionChange = (missionId: number) => {
    setMissionSelect(missionId);
    try {
      updateCompany({
        id: companyId,
        mission: missionId,
      });
    } catch (error: any) {
      toastHandler(TOAST_TYPE.ERROR_TOAST, error.data.error);
    }
  };

  const diagnosisData = useAppSelector(
    (state) => state.diagnosis.diagnosisData,
  );

  const saveProgressInDiagnosis = async () => {
    try {
      const result = await saveDiagnosis({
        company: companyId,
        diagnosisDto: diagnosisData,
        diagnosisRequirementDto: diagnosisRequirementData,
        consultor: consultorSelect ?? 0,
        diagnosis: diagnosisId ?? 0,
        mode_ejecution: ejecution,
        move_step: false,
      }).unwrap();

      // Se sincronizan los IDs del checklist despues del primer guardado
      if (
        result.checklist_requirements?.length > 0 ||
        result.checklist_questions?.length > 0
      ) {
        dispatch(
          syncChecklistIds({
            checklist_requirements: result.checklist_requirements,
            checklist_questions: result.checklist_questions,
          }),
        );
      }

      message.success("Progreso guardado correctamente");
    } catch (error) {
      message.error(
        "Error interno del sistema, comuniquese con un Administrador",
      );
    }
  };

  const confirm = async () => {
    try {
      switch (current) {
        case 0: {
          if (!diagnosisDataById) {
            message.error("No se encontraron datos del diagnostico");
            return;
          }
          const data = await saveAnswerCuestions({
            diagnosisId: diagnosisDataById.diagnosis?.id,
            // company: companyId,
            fleet_count: vehicleData,
            driver_count: driverData,
            // consultor: consultorSelect ?? 0,
          }).unwrap();
          message.success("Conteo Actualizado correctamente");
          navigate(
            `/app/companies/diagnosis/${encryptId(
              companyId.toString(),
            )}?diagnosis=${encryptId(data.diagnosis_count.diagnosis.toString())}`,
          );
          if (diagnosisDataById) dispatch(setNextDiagnosisCurrent());
          break;
        }
        case 1: {
          // ================= ADITIONAL LOGIC AND VALIDATION =============
          /**
           * This adtional sections when a user want save the process checklist
           * is only for the diagnosis process. Unique on the Diagnosis process
           * exist a validation aditional over the requirement 13 and the question
           * 68.
           *  - WHEN THE QUESTION 68 "¿Para el caso de las concesiones viales,
           *  aplica para el tramo concesionado, los requisitos mínimos: inspecciones
           *  de seguridad vial en los puntos críticos y de mayor siniestralidad vial
           *  de acuerdo con la metodología definida por el Ministerio de transporte?"
           *  HAVE THE COMPLIANCE VALUE in 4 =>"NO APLICA" THE QUESTION AUTOMATICLY
           *  SPLIT THE VALUE OBTAINED => 40 IN 2 => 20, 20 AND THIS VALUE IS SUM TO
           *  THE 2 PREVIOUS QUESTIONS => 66 AND 67, SO THE 66 AND 67 OBTAINED VALUES
           *  ARE SUM +20 EACH ONE.
           */
          let processedDiagnosisData = diagnosisData;
          const requirement13 = diagnosisRequirementData.find(
            (req) => req.requirement === 13,
          );

          if (requirement13) {
            const question68 = diagnosisData.find((q) => q.question === 68);

            if (question68 && question68.compliance === 4) {
              const valueToAdd = question68.obtained_value / 2;

              processedDiagnosisData = diagnosisData.map((q) => {
                if (q.question === 66 || q.question === 67) {
                  return {
                    ...q,
                    obtained_value: (q.obtained_value || 0) + valueToAdd,
                  };
                }

                if (q.question === 68) {
                  return {
                    ...q,
                    obtained_value: 0,
                  };
                }

                return q;
              });
            }
          }

          // ==============================================================

          dispatch(setCheckDiagnosisDataForChanges());
          await saveDiagnosis({
            company: companyId,
            diagnosisDto: processedDiagnosisData,
            diagnosisRequirementDto: diagnosisRequirementData,
            consultor: consultorSelect ?? 0,
            diagnosis: diagnosisId ?? 0,
            mode_ejecution: ejecution,
            move_step: true,
          }).unwrap();
          dispatch(setNextDiagnosisCurrent());
          message.success("Lista de chequeo guardada :)");
          break;
        }
      }
    } catch (error: any) {
      // Mostrar mensaje de error
      //(error);
      message.error(
        "Error interno del sistema, comuniquese con un Administrador",
      );
    }
  };
  const handleUpdateDataOfDiagnosis = async () => {
    try {
      if (!isOutOfContext) {
        await updateDiagnosis({
          id: diagnosisDataById!.diagnosis.id,
          ...(size > 0 && { type: size }),
          ...(observationChanged != null && { observation: observationChanged }),
        }).unwrap();

        // Se limpian los IDs de checklist antes del refetch (volver a IDs maestros)
        dispatch(fullResetDiagnosis());

        // ejecutará después de que updateAudit termine
        refetchDiagnosis();
        setUserChanged(false);
      } else if (isExternal) {
        await saveAnswerCuestions({
          // company: companyId,
          fleet_count: vehicleData,
          driver_count: driverData,
          // consultor: consultorSelect ?? 0,
          external_count_complete: true,
        }).unwrap();
        dispatch(setNextExternalCurrent());
        message.success("Conteo Actualizado correctamente");
      } else {
        await saveCountForCompanyInCorporate({
          company: companyId,
          consultor: consultorSelect ?? 0,
          corporate: corporateId ?? 0,
          driverData,
          vehicleData,
        });
        message.success("Conteo Actualizado correctamente");
      }
    } catch (error) {
      message.error(
        "Error interno del sistema, comuníquese con un Administrador",
      );
    }
  };

  const vehicleData = useAppSelector(
    (state) => state.vehicleQuestion.fleetData,
  );
  const driverData = useAppSelector((state) => state.driverQuestion.driverData);

  const diagnosisRequirementData = useAppSelector(
    (state) => state.diagnosis.diagnosisRequirementData,
  );

  const totalGeneral = totalDrivers + totalVehicles;

  const existCompany = companyId ? true : false;
  const handleSegmentChange = (newEjecution: string) => {
    setEjecution(newEjecution); // Mark that the user has manually changed the segment
  };
  const renderConsultantAndPESV = () => (
    <>
      {!isOutOfContext && (
        <div className="flex flex-col w-full col-span-6 gap-4 mt-4">
          <MissionSelect
            missionSelect={missionSelect}
            setMissionSelect={onMissionChange}
          />
          <ConsultandSelect
            consultorSelect={consultorSelect}
            setConsultorSelect={setConsultorSelect}
          />
          <Segmented
            options={[
              {
                label: "Presencial",
                value: "presencial",
              },
              {
                label: "Virtual",
                value: "virtual",
              },
            ]}
            value={ejecution}
            block
            className="col-span-6"
            onChange={handleSegmentChange}
          />
        </div>
      )}
      {existCompany && (
        <Suspense
          fallback={
            <>
              <div className="flex w-full">
                <Skeleton className="p-4 w-full" />
              </div>
            </>
          }
        >
          <PesvNoteAndSegmented
            size={size}
            setSize={setSize}
            userChanged={userChanged}
            observationChanged={observationChanged}
            setObservationChanged={setObservationChanged}
            setUserChanged={setUserChanged}
            missionId={missionSelect}
            is_in_count={current == 0 || isOutOfContext ? true : false}
            isExternal={isExternal}
          />
        </Suspense>
      )}
      {current > 0 && (
        <Button
          className="col-span-6"
          htmlType="button"
          onClick={saveProgressInDiagnosis}
          loading={saveProgressLoad}
        >
          Guardar Progreso
        </Button>
      )}
      <ContinueOrSaveButton
        userChanged={userChanged}
        totalGeneral={totalGeneral}
        consultorSelect={consultorSelect}
        isLoading={isLoading}
        confirm={confirm}
        updateDiagnosisLoading={updateDiagnosisLoading}
        handleUpdateDataOfDiagnosis={handleUpdateDataOfDiagnosis}
        isOutOfContext={isOutOfContext}
        corporateLoading={corporateLoading}
        isExternal={isExternal}
      />
    </>
  );

  return (
    <div
      className={clsx(
        "grid grid-cols-6 gap-2 md:sticky top-2 w-full md:w-[30%] md:ml-8 relative transition",
        isCompanyInfoCollapse && "!w-0 overflow-hidden !ml-0 transition",
      )}
    >
      {current > 1 && (
        <Button
          className="col-span-6"
          htmlType="button"
          color="cyan"
          danger
          onClick={() => dispatch(setPrevDiagnosisCurrent())}
        >
          Atras
        </Button>
      )}
      <CompanyHeaderInfo
        company={company}
        corporate_group={corporateData}
        isOutOfContext={isOutOfContext}
        isExternal={isExternal}
      />
      {isLoadingFleetByCompany ||
        (isLoadingDriverByCompany && <Skeleton className="w-40 h-60" />)}
      {isOutOfContext && !onlyInfo
        ? renderConsultantAndPESV()
        : current < stepsLenght - 1 && renderConsultantAndPESV()}
    </div>
  );
}
